import {useRef, useState} from 'react';
import type {SupabaseClient} from '../lib/supabaseTypes.js';
import {setHousingAnchorFromReport} from '../lib/layerHousing.js';

export type LayerAnchorReport = {batch_label?: unknown; layer_count?: unknown; date?: unknown};
export type LayerAnchorFailure = {label: string; count: number; date: string; message: string};
type AnchorWriter = typeof setHousingAnchorFromReport;

// The daily report has already committed. This owner may only retry its count
// update; it never inserts or edits the report again.
export async function settleLayerReportAnchors(
  client: SupabaseClient,
  reports: readonly LayerAnchorReport[],
  write: AnchorWriter = setHousingAnchorFromReport,
) {
  const failures: LayerAnchorFailure[] = [];
  const attempted: string[] = [];
  for (const report of reports) {
    const label = typeof report.batch_label === 'string' ? report.batch_label : '';
    const date = typeof report.date === 'string' ? report.date : '';
    const count = parseInt(String(report.layer_count));
    if (!label || !date || !Number.isFinite(count) || count < 0) continue;
    attempted.push(label.trim().toLowerCase());
    let message = 'The housing count could not be updated. Retry the count update.';
    try {
      const result = await write(client, label, count, date);
      if (result?.ok === true) continue;
      if (result?.reason === 'ambiguous-batch') {
        message = 'This batch has multiple active housings. Set the count on the correct housing.';
      } else if (result?.reason === 'no-match') {
        message = 'No matching housing was found. Check the housing name before retrying.';
      }
    } catch {
      // A rejected transport is still a failed count update, not a failed report.
    }
    failures.push({label, count, date, message});
  }
  return {failures, attempted};
}

export function useLayerReportAnchors(client: SupabaseClient) {
  const [pending, setPending] = useState<LayerAnchorFailure[]>([]);
  const pendingRef = useRef<LayerAnchorFailure[]>([]);
  const [busy, setBusy] = useState(false);
  const tail = useRef<Promise<unknown>>(Promise.resolve());
  const waiting = useRef(0);
  function apply(reports: readonly LayerAnchorReport[]) {
    waiting.current += 1;
    setBusy(true);
    const run = tail.current.then(async () => {
      const result = await settleLayerReportAnchors(client, reports);
      // A later saved count for the same group supersedes its earlier retry.
      pendingRef.current = [
        ...pendingRef.current.filter((item) => !result.attempted.includes(item.label.trim().toLowerCase())),
        ...result.failures,
      ];
      setPending(pendingRef.current);
      return result.failures;
    });
    tail.current = run.catch(() => {});
    return run.finally(() => {
      waiting.current -= 1;
      if (waiting.current === 0) setBusy(false);
    });
  }
  function retry() {
    if (waiting.current) return Promise.resolve(pendingRef.current);
    return apply(pendingRef.current.map(({label, count, date}) => ({batch_label: label, layer_count: count, date})));
  }
  return {pending, busy, apply, retry};
}
